import { useEffect, useMemo, useState, Fragment } from "react";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, Circle, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import { idleInfo } from "@/lib/idle";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Navigation, ClipboardList, Building2, AlertTriangle, Layers } from "lucide-react";

const STATUS_COLOR = { scheduled: "#0D9488", active: "#16A34A", completed: "#64748B", cancelled: "#DC2626" };
const localDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const daysFrom = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return localDay(d); };

const pin = (colour) => L.divIcon({
  className: "",
  html: `<div style="background:${colour};width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.5)"><span style="display:block;width:10px;height:10px;background:#fff;border-radius:50%;margin:7.5px auto"></span></div>`,
  iconSize: [30, 30], iconAnchor: [15, 28], popupAnchor: [0, -26],
});

const queueIcon = L.divIcon({
  className: "",
  html: `<div style="background:#7C3AED;width:24px;height:24px;border-radius:6px;transform:rotate(45deg);border:2.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.5)"></div>`,
  iconSize: [24, 24], iconAnchor: [12, 12], popupAnchor: [0, -14],
});

const dot = (colour, ring) => L.divIcon({
  className: "",
  html: `<div style="position:relative;width:22px;height:22px">${ring ? `<div style="position:absolute;inset:-5px;border-radius:50%;border:2px solid ${colour};opacity:.4"></div>` : ""}<div style="background:${colour};width:22px;height:22px;border-radius:50%;border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5)"></div></div>`,
  iconSize: [22, 22], iconAnchor: [11, 11], popupAnchor: [0, -12],
});


const BASEMAPS = {
  standard: {
    label: "Map",
    layers: [{ url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: "&copy; OpenStreetMap" }],
  },
  satellite: {
    label: "Satellite",
    layers: [{ url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "Tiles &copy; Esri" }],
  },
  hybrid: {
    label: "Hybrid",
    layers: [
      { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "Tiles &copy; Esri" },
      { url: "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}", attribution: "" },
    ],
  },
  dark: {
    label: "Dark",
    layers: [
      { url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}", attribution: "Tiles &copy; Esri" },
      { url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}", attribution: "" },
    ],
  },
};

function ClickCatcher({ onPick }) {
  useMapEvents({ click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
}

function FitBounds({ points }) {
  const map = useMap();
  const key = points.map((p) => `${p.lat},${p.lng}`).join("|");
  useEffect(() => {
    if (points.length) map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])), { padding: [50, 50], maxZoom: 14 });
    // refit only when the set of points changes, not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);
  return null;
}

export default function FieldMap() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [start, setStart] = useState(localDay(new Date()));
  const [end, setEnd] = useState(daysFrom(14));
  const [statusF, setStatusF] = useState("all");
  const [showConnect, setShowConnect] = useState(false);
  const [showField, setShowField] = useState(true);
  const [showClockPoints, setShowClockPoints] = useState(false);
  const [showLive, setShowLive] = useState(false);
  const [outsideOnly, setOutsideOnly] = useState(false);
  const [points, setPoints] = useState([]);
  const [live, setLive] = useState([]);
  const [baseMap, setBaseMap] = useState("standard");
  const [pick, setPick] = useState(null);
  const [showGeofences, setShowGeofences] = useState(true);
  const [allSites, setAllSites] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [dayOnly, setDayOnly] = useState(false);
  const [day, setDay] = useState(localDay(new Date()));
  const [showCommunal, setShowCommunal] = useState(false);
  const [communal, setCommunal] = useState([]);
  const [showQueue, setShowQueue] = useState(false);
  const [queued, setQueued] = useState([]);
  useEffect(() => {
    if (!showQueue) { setQueued([]); return; }
    client.get("/job-queue").then((r) => setQueued((r.data || []).filter((q) => q.geo?.lat != null && q.geo?.lng != null && !(q.geo.lat === 0 && q.geo.lng === 0)))).catch(() => setQueued([]));
  }, [showQueue]);
  const qStart = dayOnly ? day : start;
  const qEnd = dayOnly ? day : end;

  useEffect(() => {
    client.get("/sites").then((r) => setAllSites(r.data || [])).catch(() => setAllSites([]));
    client.get("/properties").then((r) => setAllProperties(r.data || [])).catch(() => setAllProperties([]));
  }, []);

  useEffect(() => {
    client.get(`/calendar?start=${qStart}&end=${qEnd}`).then((r) => setEvents(r.data || [])).catch(() => setEvents([]));
  }, [qStart, qEnd]);

  useEffect(() => {
    if (!showClockPoints) { setPoints([]); return; }
    client.get(`/attendance/points?start=${qStart}&end=${qEnd}`).then((r) => setPoints(r.data.points || [])).catch(() => setPoints([]));
  }, [showClockPoints, qStart, qEnd]);

  useEffect(() => {
    if (!showCommunal) { setCommunal([]); return; }
    client.get("/communal-list").then((r) => setCommunal((r.data || []).filter((p) => p.geo?.lat != null && p.geo?.lng != null && !(p.geo.lat === 0 && p.geo.lng === 0)))).catch(() => setCommunal([]));
  }, [showCommunal]);

  useEffect(() => {
    if (!showLive) { setLive([]); return; }
    const load = () => client.get("/attendance/live").then((r) => setLive(r.data.staff || [])).catch(() => setLive([]));
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [showLive]);

  const [searchParams] = useSearchParams();
  const idleOnly = searchParams.get("idle") === "1";
  useEffect(() => { if (idleOnly) setShowLive(true); }, [idleOnly]);

  const jobs = useMemo(() => events.filter((e) => e.kind === "job"), [events]);

  const matchStatus = (e) => (statusF === "all" || e.status === statusF) && !(dayOnly && e.status === "cancelled");
  const scheduledIds = useMemo(() => new Set(events.filter((e) => e.status !== "cancelled").map((e) => (e.kind === "job" ? e.propertyId : e.siteId)).filter(Boolean)), [events]);

  const shown = showField ? jobs.filter(matchStatus) : [];
  const withGeo = shown.filter((e) => e.lat != null && e.lng != null);
  const noGeo = shown.filter((e) => e.lat == null || e.lng == null);
  const connectWithGeo = useMemo(() => showConnect
    ? allSites
        .filter((s) => s.geo?.lat != null && s.geo?.lng != null && !(s.geo.lat === 0 && s.geo.lng === 0))
        .filter((s) => !dayOnly || scheduledIds.has(s.id))
        .map((s, i) => ({ id: s.id || s._id || `site-${i}`, title: s.name, lat: s.geo.lat, lng: s.geo.lng, address: s.address }))
    : [], [showConnect, allSites, dayOnly, scheduledIds]);
  const outsideCount = points.filter((p) => p.withinGeofence === false).length;
  const shownPoints = outsideOnly ? points.filter((p) => p.withinGeofence === false) : points;

  const geofences = useMemo(() => {
    const out = [];
    const add = (geo, radius, enabled, title, kind, id) => {
      const lat = geo?.lat, lng = geo?.lng;
      if (lat == null || lng == null || (lat === 0 && lng === 0)) return;
      if (dayOnly && !scheduledIds.has(id)) return;
      out.push({ lat, lng, radius: (radius && radius > 0) ? radius : 25, enabled: !!enabled, title: title || "—", kind });
    };
    allSites.forEach((s) => add(s.geo, s.geofenceRadius, s.geofenceEnabled, s.name, "Connect site", s.id));
    allProperties.forEach((p) => add(p.geo, p.geofenceRadius, p.geofenceEnabled, p.address, "Property", p.id));
    return out;
  }, [allSites, allProperties, dayOnly, scheduledIds]);

  const center = withGeo[0] ? [withGeo[0].lat, withGeo[0].lng] : [51.5074, -0.1278];
  const FILTERS = ["all", "scheduled", "active", "completed"];

  return (
    <div className="space-y-4" data-testid="field-map-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Badge className="bg-brand/10 text-brand border-brand/20 mb-1">{t("field.label", "iClean Field")}</Badge>
          <h2 className="font-display text-2xl font-bold text-brand-ink">{t("map.title", "Field Map")}</h2>
          <p className="text-sm text-slate-500">{t("map.subtitle", "Field jobs and their properties on the map.")}</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <label className={`flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-semibold cursor-pointer ${dayOnly ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-slate-600"}`}>
            <input type="checkbox" data-testid="map-day-only" checked={dayOnly} onChange={(e) => setDayOnly(e.target.checked)} />
            Scheduled on one day only
          </label>
          {dayOnly ? (
            <div><label className="text-xs text-slate-500">Day</label><Input type="date" data-testid="map-day" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="h-9" /></div>
          ) : (
            <>
              <div><label className="text-xs text-slate-500">{t("ts.from", "From")}</label><Input type="date" data-testid="map-start" value={start} onChange={(e) => setStart(e.target.value)} className="h-9" /></div>
              <div><label className="text-xs text-slate-500">{t("ts.to", "To")}</label><Input type="date" data-testid="map-end" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9" /></div>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="map-filters">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`map-filter-${f}`} onClick={() => setStatusF(f)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition ${statusF === f ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {t(`map.f.${f}`, { all: "All", scheduled: "Scheduled", active: "Active", completed: "Completed" }[f])}
          </button>
        ))}
        <label className="ml-2 flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" data-testid="map-show-field" checked={showField} onChange={(e) => setShowField(e.target.checked)} />
          <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full" style={{ background: "#EA731D" }} />{t("map.showField", "Field jobs")}</span>
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" data-testid="map-show-connect" checked={showConnect} onChange={(e) => setShowConnect(e.target.checked)} />
          <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full" style={{ background: "#0D9488" }} />{t("map.showConnect", "Connect sites")}</span>
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" data-testid="map-show-clockpoints" checked={showClockPoints} onChange={(e) => { setShowClockPoints(e.target.checked); if (!e.target.checked) setOutsideOnly(false); }} />
          {t("map.showClockPoints", "Clock in/out points")}
        </label>
        <label className={`flex items-center gap-1.5 text-xs cursor-pointer ${outsideOnly ? "text-red-600 font-semibold" : "text-slate-600"}`}>
          <input type="checkbox" data-testid="map-outside-only" checked={outsideOnly} onChange={(e) => { setOutsideOnly(e.target.checked); if (e.target.checked) setShowClockPoints(true); }} />
          {t("map.outsideOnly", "Outside geofence only")}
          {showClockPoints && outsideCount > 0 && <span data-testid="map-outside-count" className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">{outsideCount}</span>}
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" data-testid="map-show-live" checked={showLive} onChange={(e) => setShowLive(e.target.checked)} />
          {t("map.showLive", "Live staff on shift")}
          {showLive && <span className="ml-1 inline-flex h-2 w-2 rounded-full bg-blue-500 animate-pulse" />}
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" data-testid="map-show-communal" checked={showCommunal} onChange={(e) => setShowCommunal(e.target.checked)} />
          <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full" style={{ background: "#7C3AED" }} />Communal area cleaning</span>
          {showCommunal && <span data-testid="map-communal-count" className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold">{communal.length}</span>}
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" data-testid="map-show-queue" checked={showQueue} onChange={(e) => setShowQueue(e.target.checked)} />
          <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rotate-45" style={{ background: "#7C3AED" }} />Queued jobs</span>
          {showQueue && <span data-testid="map-queue-count" className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold">{queued.length}</span>}
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" data-testid="map-show-geofences" checked={showGeofences} onChange={(e) => setShowGeofences(e.target.checked)} />
          {t("map.showGeofences", "Geofences")}
        </label>
        {showGeofences && (
          <span className="flex items-center gap-3 text-[11px] text-slate-500" data-testid="map-geofence-legend">
            <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#16A34A" }} />{t("map.geofenceOn", "Enforced")}</span>
            <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full border border-dashed" style={{ borderColor: "#94A3B8", background: "#94A3B833" }} />{t("map.geofenceOff", "Not enforced")}</span>
          </span>
        )}
      </div>

      <Card className="overflow-hidden border-slate-200 relative isolate" data-testid="map-container">
        <div className="absolute top-2 right-2 z-[1000] flex rounded-lg border border-slate-200 overflow-hidden shadow-sm bg-white" data-testid="map-basemap-switch">
          {Object.entries(BASEMAPS).map(([k, v]) => (
            <button key={k} data-testid={`map-base-${k}`} onClick={() => setBaseMap(k)}
              className={`px-2.5 py-1.5 text-xs font-semibold flex items-center gap-1 ${baseMap === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              {k === "standard" && <Layers className="h-3 w-3" />}{t(`map.base.${k}`, v.label)}
            </button>
          ))}
        </div>
        <div style={{ height: "62vh", width: "100%" }}>
          <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            {BASEMAPS[baseMap].layers.map((l, i) => (
              <TileLayer key={`${baseMap}-${i}`} attribution={l.attribution} url={l.url} />
            ))}
            <ClickCatcher onPick={setPick} />
            <FitBounds points={showGeofences ? [...withGeo, ...connectWithGeo, ...geofences] : [...withGeo, ...connectWithGeo]} />
            {showGeofences && geofences.map((g, i) => (
              <Fragment key={`gf-${i}`}>
                <Circle center={[g.lat, g.lng]} radius={g.radius}
                  pathOptions={{
                    color: g.enabled ? "#16A34A" : "#94A3B8",
                    fillColor: g.enabled ? "#16A34A" : "#94A3B8",
                    fillOpacity: 0.12, weight: 2, dashArray: g.enabled ? undefined : "5 5",
                  }} />
                <Marker position={[g.lat, g.lng]} icon={pin(g.kind === "Property" ? "#EA731D" : "#0D9488")} data-testid={`map-site-pin-${i}`}>
                  <Popup>
                    <div className="text-sm">
                      <p className="font-semibold flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{g.title}</p>
                      <p className="text-xs text-slate-500">{g.kind} · {g.radius}m · {g.enabled ? t("map.geofenceOn", "Enforced") : t("map.geofenceOff", "Not enforced")}</p>
                    </div>
                  </Popup>
                </Marker>
              </Fragment>
            ))}
            {shownPoints.map((p) => (
              <Marker key={`p-${p.id}`} position={[p.lat, p.lng]} icon={dot(p.withinGeofence === false ? "#DC2626" : (p.type === "in" ? "#16A34A" : "#F59E0B"))} data-testid={`map-clockpoint-${p.id}`}>
                <Popup>
                  <div className="text-sm min-w-[180px]">
                    <p className="font-semibold">{p.workerName}</p>
                    <p className="text-xs">{p.type === "in" ? "Clocked in" : "Clocked out"} · {p.kind === "site" ? (p.siteName || "Site") : "Field job"}</p>
                    <p className="text-xs text-slate-500">{new Date(p.at).toLocaleString()}</p>
                    {p.withinGeofence === false && <p className="text-xs text-red-600 font-semibold flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Outside geofence</p>}
                  </div>
                </Popup>
              </Marker>
            ))}
            {(idleOnly ? live.filter((s) => idleInfo(s.pingAt).isIdle) : live).map((s) => {
              const { isIdle, idleMinutes } = idleInfo(s.pingAt);
              return (
              <Marker key={`l-${s.workerId}`} position={[s.lat, s.lng]} icon={dot(isIdle ? "#F59E0B" : "#16A34A", true)} data-testid={`map-live-${s.workerId}`}>
                {isIdle && <Tooltip permanent direction="top" offset={[0, -8]} className="idle-tooltip">Idle {idleMinutes}m</Tooltip>}
                <Popup>
                  <div className="text-sm min-w-[180px]">
                    <p className="font-semibold">{s.workerName}</p>
                    <p className="text-xs text-emerald-600 font-medium">● On shift{s.kind === "job" ? " (field job)" : s.siteName ? ` · ${s.siteName}` : ""}</p>
                    <p className="text-xs text-slate-500">Since {new Date(s.since).toLocaleTimeString()}{s.pingAt ? ` · updated ${new Date(s.pingAt).toLocaleTimeString()}` : ""}</p>
                    {isIdle && <p className="mt-1 text-xs font-semibold" style={{ color: "#F59E0B" }} data-testid={`map-live-idle-${s.workerId}`}>⚠ Idle {idleMinutes}m — no recent GPS ping</p>}
                  </div>
                </Popup>
              </Marker>
              );
            })}
            {connectWithGeo.map((e) => (
              <Marker key={`s-${e.id}`} position={[e.lat, e.lng]} icon={pin("#0D9488")} data-testid={`map-connect-${e.id}`}>
                <Popup>
                  <div className="text-sm">
                    <p className="font-semibold flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{e.title} <Badge className="ml-1 bg-teal-50 text-teal-700 border-teal-200">Connect</Badge></p>
                    {e.address && <p className="text-xs text-slate-500">{e.address}</p>}
                  </div>
                </Popup>
              </Marker>
            ))}
            {withGeo.map((e) => {
              const op = e.operational || {};
              const col = STATUS_COLOR[e.status] || "#EA731D";
              return (
                <Marker key={`j-${e.id}`} position={[e.lat, e.lng]} icon={pin(col)} data-testid={`map-marker-${e.id}`}>
                  <Popup>
                    <div className="text-sm space-y-1 min-w-[200px]" data-testid={`map-popup-${e.id}`}>
                      <p className="font-semibold flex items-center gap-1"><ClipboardList className="h-3.5 w-3.5 text-brand" />{op.clientName || e.title} <Badge className="ml-1 bg-brand/10 text-brand border-brand/20">Field</Badge></p>
                      <p className="text-xs text-slate-600">{op.address || e.title}</p>
                      <p className="text-xs text-slate-500">{e.date} · {e.startTime}–{e.endTime}</p>
                      <p className="text-xs capitalize text-slate-500">{e.status}</p>
                      {(op.workers || []).length > 0 && <p className="text-xs text-slate-500">{(op.workers).map((w) => w.name || w).join(", ")}</p>}
                      <div className="flex gap-2 pt-1">
                        <button className="text-xs font-semibold text-brand hover:underline" data-testid={`map-open-${e.id}`} onClick={() => nav("/jobs")}>{t("map.openJob", "Open Job")}</button>
                        <a className="text-xs font-semibold text-teal-600 hover:underline flex items-center gap-1" target="_blank" rel="noreferrer"
                          href={`https://www.google.com/maps/dir/?api=1&destination=${e.lat},${e.lng}`}><Navigation className="h-3 w-3" />{t("map.directions", "Directions")}</a>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            {communal.map((p) => {
              const booked = scheduledIds.has(p.id);
              return (
                <Marker key={`c-${p.id}`} position={[p.geo.lat, p.geo.lng]} icon={pin("#7C3AED")} zIndexOffset={-100} data-testid={`map-communal-${p.id}`}>
                  <Popup>
                    <div className="text-sm space-y-1 min-w-[200px]">
                      <p className="font-semibold flex items-center gap-1"><Building2 className="h-3.5 w-3.5 text-violet-600" />{p.address}</p>
                      <p className="text-xs text-slate-600">{p.clientName} · Communal area cleaning</p>
                      <p className="text-xs text-slate-500">Last visit: {p.lastVisit || "—"} · Next booked: {p.nextVisit || "none"}</p>
                      {booked && <p className="text-xs font-semibold text-emerald-700" data-testid={`map-communal-booked-${p.id}`}>✓ Already booked {dayOnly ? "this day" : "in this date range"}</p>}
                      <button className="text-xs font-semibold text-brand hover:underline" data-testid={`map-communal-open-${p.id}`} onClick={() => nav(`/properties/${p.id}`)}>Open property to book</button>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            {queued.map((q) => (
              <Marker key={`q-${q.id}`} position={[q.geo.lat, q.geo.lng]} icon={queueIcon} data-testid={`map-queue-${q.id}`}>
                <Popup>
                  <div className="text-sm space-y-1 min-w-[200px]">
                    <p className="font-semibold flex items-center gap-1"><ClipboardList className="h-3.5 w-3.5 text-violet-600" />{q.address}</p>
                    <p className="text-xs text-slate-600">{q.clientName} · Queued · needs {Math.round((q.durationMinutes || 120) / 6) / 10}h</p>
                    <button className="text-xs font-semibold text-brand hover:underline" data-testid={`map-queue-open-${q.id}`} onClick={() => nav("/schedule")}>Book it on the schedule</button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </Card>

      {noGeo.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50/40" data-testid="map-no-gps">
          <p className="text-sm font-semibold text-amber-700 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" />{t("map.noGps", "Jobs without GPS coordinates")} ({noGeo.length})</p>
          <div className="mt-2 space-y-1">
            {noGeo.map((e) => (
              <div key={e.id} className="flex items-center justify-between text-sm text-slate-600 px-2 py-1.5 rounded hover:bg-white cursor-pointer" onClick={() => nav("/jobs")}>
                <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-slate-400" />{(e.operational || {}).clientName || e.title} · {e.date}</span>
                <Badge variant="outline" className="capitalize">{e.status}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
      <Dialog open={!!pick} onOpenChange={(o) => !o && setPick(null)}>
        <DialogContent data-testid="map-add-dialog">
          <DialogHeader><DialogTitle>{t("map.addHere", "Add here")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">
            {t("map.addHint", "Create new work at this location.")}
            {pick && <span className="block font-mono text-[11px] text-slate-400 mt-1">{pick.lat.toFixed(5)}, {pick.lng.toFixed(5)}</span>}
          </p>
          <div className="grid grid-cols-2 gap-3 pt-1">
            <Button data-testid="map-add-job" className="bg-brand hover:bg-brand-dark h-auto py-3 flex-col gap-1"
              onClick={() => nav("/jobs")}>
              <ClipboardList className="h-5 w-5" />{t("map.addJob", "New Field Job")}
            </Button>
            <Button data-testid="map-add-shift" variant="outline" className="border-teal-200 text-teal-700 hover:bg-teal-50 h-auto py-3 flex-col gap-1"
              onClick={() => nav("/schedule")}>
              <Building2 className="h-5 w-5" />{t("map.addShift", "New Connect Shift")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
